import { EdgeData, EdgeProps, NodeChildProps, NodeData, NodeProps } from "reaflow";
import StartNode from "./Node/StartNode";
import EndNode from "./Node/EndNode";
import DefaultNode from "./Node/DefaultNode";
import GroupNode from "./Node/GroupNode";
import { showNotification } from "@mantine/notifications";
import { UAction, UNode, UNodeType } from "@supportinitiative/common";
import { IDispatchAction } from "src/types";
import { getId } from "src/utilities";

export type CanvasAction = "zoomIn" | "zoomOut" | "zoomReset";

// Node

export type BaseNodeAdditionalDataType<T extends UNodeType = UNodeType> = {
  type: T;
  name: string;
  description: string;
};

export type BaseNodeDataType<T extends BaseNodeAdditionalDataType = BaseNodeAdditionalDataType> = NodeData<T>;

// Props passed to base node component
export type BaseNodePropsType<T extends BaseNodeDataType = BaseNodeDataType> = {
  /**
   * Current node.
   */
  node: T;
  allNodes: BaseNodeDataType[];
  allEdges: BaseEdgeDataType[];
  selectedNode?: BaseNodeDataType | null;
  isDisabled?: boolean;
  onNodeClick(node: BaseNodeDataType): void;
  onDeleteNode(node: BaseNodeDataType): void;
  onPortDragEnd(fromNodeId: string, toNodeId?: string): void;
  onGroupAdd(node: BaseNodeDataType): void;
} & Partial<NodeProps>;

export type BaseNodeComponentType<T extends BaseNodePropsType = BaseNodePropsType> = React.FC<T>;

export type SpecializedNodeProps<NodeData extends BaseNodeDataType = BaseNodeDataType> = Omit<
  NodeChildProps,
  "node"
> & {
  node: NodeData;
};

export type BaseEdgeAdditionalDataType = {};

export type BaseEdgeDataType = EdgeData<Required<BaseEdgeAdditionalDataType>>;

export type BaseEdgePropsType = Partial<EdgeProps> & {
  allNodes: BaseNodeDataType[];
  allEdges: BaseEdgeDataType[];
  selectedEdge?: BaseEdgeDataType | null;
  isDisabled?: boolean;
  onAddNode(edge: BaseEdgeDataType): void;
  onDeleteEdge(edge: BaseEdgeDataType): void;
  onEdgeClick(edge: BaseEdgeDataType): void;
};

export const findNodeComponentByType = (nodeType: UNodeType): BaseNodeComponentType => {
  switch (nodeType.toUpperCase()) {
    case "START":
      return StartNode;
    case "END":
      return EndNode;
    case "GROUP":
      return GroupNode;
    case "SERIAL_GROUP":
      return GroupNode;
    case "PARALLEL_GROUP":
      return GroupNode;
    default:
      return DefaultNode;
  }
};

export function getNodeLabel(type: string) {
  switch (type.toUpperCase()) {
    case "RECONCILIATION": {
      return "Reconciliation";
    }
    case "MONITORING": {
      return "Monitoring";
    }
    case "DATALOAD": {
      return "Dataload";
    }
    case "GROUP": {
      return "Group";
    }
    default:
      return type;
  }
}

export function getDefaultWorkflow() {
  const startId = getId(5),
    endId = getId(5);

  const nodes: BaseNodeDataType[] = [
    {
      id: startId,
      width: 100,
      height: 100,
      ports: [{ id: `port-${startId}-start`, height: 10, width: 10, side: "EAST" }],
      data: {
        name: "Start",
        type: "START",
        description: "Workflow start",
      },
    },
    {
      id: endId,
      width: 100,
      height: 100,
      ports: [{ id: `port-${endId}-end`, height: 10, width: 10, side: "WEST" }],
      data: {
        name: "End",
        type: "END",
        description: "Workflow end",
      },
    },
  ];

  const edges: BaseEdgeDataType[] = [
    {
      id: `${startId}-${endId}`,
      from: startId,
      to: endId,
      // text: " ",
    },
  ];

  const actions: UAction[] = [
    {
      id: startId,
      name: "Start",
      type: "START",
      description: "Workflow start",
      definition: {},
    },
    {
      id: endId,
      name: "End",
      type: "END",
      description: "Workflow end",
      definition: {},
    },
  ];

  return {
    nodes,
    edges,
    actions,
  };
}

// --- Helpers
export function addViaEdge(
  newNode: BaseNodeDataType,
  edge: BaseEdgeDataType,
  nodes: BaseNodeDataType[],
  edges: BaseEdgeDataType[],
  actions: UAction[],
) {
  // For now, we are blocking adding nodes via edges that are part of a group
  // Some part of code to add to group is present below, but it is not fully implemented yet
  // All edge cases are not handled yet, so we are blocking it for now
  if (edge.parent) {
    showNotification({
      id: "error",
      title: "Add via edge",
      message: "Cannot add node via edge that is part of a group",
      color: "red",
    });
  }

  const updatedNodes = [...nodes, { ...newNode, parent: edge.parent }];

  // --- Insert new node beetween two existing ones
  const fromNode = nodes.find((node) => node.id === edge.from);
  const toNode = nodes.find((node) => node.id === edge.to);

  if (!fromNode || !toNode) {
    showNotification({
      id: "error",
      title: "Add via edge",
      message: "Unable to find nodes for the edge. Please check the workflow",
      color: "red",
    });

    return {
      edges,
      nodes,
      actions,
    };
  }

  const updatedEdges = [
    ...edges.filter((e) => e.id !== edge.id),
    createEdge({
      from: edge.from,
      to: newNode.id,
      // text: " ",
      fromPort: `port_${edge.from}_end`,
      toPort: `port_${newNode.id}_start`,
      parent: edge.parent,
    }),
    createEdge({
      from: newNode.id,
      to: edge.to,
      // text: " ",
      fromPort: `port_${newNode.id}_end`,
      toPort: `port_${edge.to}_start`,
      parent: edge.parent,
    }),
  ];

  let newAction: UAction;

  newAction = {
    id: newNode.id,
    type: newNode.data?.type as UNodeType,
    name: newNode.data?.name as string,
    definition: {},
  };

  const updatedActions = [...actions, newAction];

  return {
    edges: updatedEdges,
    nodes: updatedNodes,
    actions: updatedActions,
  };
}

export function addNodeViaPort(
  newNode: BaseNodeDataType,
  port: { from: string; to?: string | null },
  nodes: BaseNodeDataType[],
  edges: BaseEdgeDataType[],
  actions: UAction[],
) {
  // console.log("addNodeViaPort", port);
  if (port.to) {
    // This case may not arise in the current implementation
    // Handled in addEdgeViaPort function
    showNotification({
      id: "error",
      title: "Add node",
      message: "Cannot add a new node via port that has a target",
      color: "red",
    });

    return {
      edges,
      nodes,
      actions,
    };

    /* const fromNode = nodes.find((node) => node.id === port.from);
    const toNode = nodes.find((node) => node.id === port.to);

    if (!fromNode) {
      return {
        edges,
        nodes,
        actions,
      };
    }

    if (!toNode) {
      return {
        edges,
        nodes,
        actions,
      };
    }

    const updatedNodes = [...nodes, newNode];
    const updatedEdges = [
      ...edges,
      createEdge({ from: port.from, to: newNode.id, text: " " }),
      createEdge({ from: newNode.id, to: port.to, text: " " }),
    ];

    const newAction = {
      id: newNode.id,
      type: newNode.data?.type,
      name: newNode.data?.name,
    };

    const updatedActions = [...actions, newAction];

    return {
      edges: updatedEdges,
      nodes: updatedNodes,
      actions: updatedActions,
    }; */
  } else {
    if (port.from === "end") {
      showNotification({
        id: "error",
        title: "Add node",
        message: `Cannot add a new node after "End" node`,
        color: "red",
      });

      return {
        edges,
        nodes,
        actions,
      };
    }
    const updatedNodes = [...nodes, newNode];

    const fromNode = nodes.find((node) => node.id === port.from);

    if (!fromNode) {
      return {
        edges,
        nodes,
        actions,
      };
    }

    // --- Insert new node beetween two existing ones
    const updatedEdges = [
      ...edges,
      createEdge({
        from: port.from,
        to: newNode.id,
        // text: " ",
        fromPort: `port_${port.from}_end`,
        toPort: `port_${newNode.id}_start`,
      }),
    ];

    const newAction: UAction = {
      id: newNode.id,
      type: newNode.data?.type as UNodeType,
      name: newNode.data?.name as string,
      definition: {
        workflow: getDefaultWorkflow(),
      },
    };

    const updatedActions = [...actions, newAction];

    return {
      edges: updatedEdges,
      nodes: updatedNodes,
      actions: updatedActions,
    };
  }
}

export function addEdgeViaPort(
  port: { from: string; to?: string | null },
  nodes: BaseNodeDataType[],
  edges: BaseEdgeDataType[],
  actions: UAction[],
) {
  // console.log("addEdgeViaPort", port);
  if (port.to) {
    const fromNode = nodes.find((node) => node.id === port.from);
    const toNode = nodes.find((node) => node.id === port.to);

    if (!fromNode) {
      showNotification({
        id: "error",
        title: "Add edge",
        message: "Unable to find from node for the edge",
        color: "red",
      });
      return {
        edges,
        nodes,
        actions,
      };
    }

    if (!toNode) {
      showNotification({
        id: "error",
        title: "Add edge",
        message: "Unable to find to node for the edge",
        color: "red",
      });
      return {
        edges,
        nodes,
        actions,
      };
    }
    if (fromNode.id === toNode.id) {
      showNotification({
        id: "error",
        title: "Add edge",
        message: "Edge cannot be added between the same node",
        color: "red",
      });
      return {
        edges,
        nodes,
        actions,
      };
    }

    if (fromNode.data?.type.toUpperCase() === "END") {
      showNotification({
        id: "error",
        title: "Add edge",
        message: "Edge cannot be added from an End node",
        color: "red",
      });
      return {
        edges,
        nodes,
        actions,
      };
    }

    if (fromNode.parent || toNode.parent) {
      if (fromNode.parent === toNode.id || toNode.parent === fromNode.id) {
        showNotification({
          id: "error",
          title: "Add edge",
          message: "Edge cannot be linked between parent and child nodes",
          color: "red",
        });
        return {
          edges,
          nodes,
          actions,
        };
      } else if (fromNode.parent && !toNode.parent) {
        showNotification({
          id: "error",
          title: "Add edge",
          message: "Edge cannot be linked from node inside group to node outside group",
          color: "red",
        });
        return {
          edges,
          nodes,
          actions,
        };
      } else if (toNode.parent && !fromNode.parent) {
        showNotification({
          id: "error",
          title: "Add edge",
          message: "Edge cannot be linked from node outside group to node inside group",
          color: "red",
        });
        return {
          edges,
          nodes,
          actions,
        };
      }
    } else {
      if (fromNode.parent !== toNode.parent) {
        showNotification({
          id: "error",
          title: "Add edge",
          message: "Edge cannot be linked between nodes that are in different groups",
          color: "red",
        });

        return {
          edges,
          nodes,
          actions,
        };
      }
    }

    // console.log("Adding edge via port", port);
    /*     let updatedEdges;
    if (toNode.parent) {
      updatedEdges = [
        ...edges,
        createEdge({
          from: port.from,
          to: toNode.parent,
          // text: " ",
          fromPort: `port_${port.from}_end`,
          toPort: `port_${toNode.parent}_start`,
          parent: undefined,
        }),
      ];
    } else {
      updatedEdges = [
        ...edges,
        createEdge({
          from: port.from,
          to: port.to,
          // text: " ",
          fromPort: `port_${port.from}_end`,
          toPort: `port_${port.to}_start`,
          parent: toNode.parent,
        }),
      ];
    }
 */
    const updatedEdges = [
      ...edges,
      createEdge({
        from: port.from,
        to: port.to,
        // text: " ",
        fromPort: `port_${port.from}_end`,
        toPort: `port_${port.to}_start`,
        parent: toNode.parent,
      }),
    ];
    // console.log("Adding edge via port", updatedEdges);

    return {
      edges: updatedEdges,
      nodes: nodes,
      actions: actions,
    };
  } else {
    // This case may not arise in the current implementation
    // Handled in addNodeViaPort function
    showNotification({
      id: "error",
      title: "Add edge",
      message: "No node to connect to",
      color: "red",
    });
    return {
      edges: edges,
      nodes: nodes,
      actions: actions,
    };
  }
}

export function removeEdge(
  edge: BaseEdgeDataType,
  nodes: BaseNodeDataType[],
  edges: BaseEdgeDataType[],
  actions: UAction[],
) {
  // --- Insert new node beetween two existing ones
  const updatedEdges = [...edges.filter((e) => e.id !== edge.id)];

  if (updatedEdges.length === 0) {
    if ((edge.from === "start" && edge.to === "end") || (edge.to === "start" && edge.from === "end")) {
      showNotification({
        id: "error",
        title: "Delete edge",
        message: "Cannot remove the default Start-End edge as no other paths present",
        color: "red",
      });

      return {
        edges: edges,
        nodes: nodes,
        actions: actions,
      };
    }
  }

  return {
    edges: updatedEdges,
    nodes: nodes,
    actions: actions,
  };
}

export function removeNode(
  node: BaseNodeDataType,
  nodes: BaseNodeDataType[],
  edges: BaseEdgeDataType[],
  actions: UAction[],
) {
  // console.log("removeNode", node);

  if (node.parent) {
    // Part of a group. Need to check if the node is connected to any other nodes in the group

    const parentNode = nodes.find((n) => n.id === node.parent);
    if (!parentNode) {
      showNotification({
        id: "error",
        title: "Remove node",
        message: "Unable to find parent node for the group",
        color: "red",
      });
      return {
        nodes,
        edges,
        actions,
      };
    }
    if (parentNode.data?.type.toUpperCase() === "SERIAL_GROUP") {
      // Check if the right side of the node is connected to any other node or to the group end
      // If connected to group end, then connect to the group end node
      // If connected to any other node, then connect to that node

      const edgeToTheRightOfNode = edges.find(({ from, to }) => from === node.id),
        edgesToRetain = edges.filter(({ from, to }) => !(from === node.id || to === node.id));

      // console.log("edgeToTheRightOfNode", edgeToTheRightOfNode);

      const newNodes = nodes.filter((n) => !(n.id === node.id)),
        newActions = actions.filter((action) => action.id !== node.id),
        newEdges: BaseEdgeDataType[] = [];

      newEdges.push(...edgesToRetain);

      if (!edgeToTheRightOfNode) {
        showNotification({
          id: "error",
          title: "Remove node",
          message: "Unable to find edges connected to the right of the node being removed",
          color: "red",
        });
        return {
          nodes: nodes,
          edges: edges,
          actions: actions,
        };
      }

      // Get the node to the left of the node being removed
      const edgeToTheLeftOfNode = edges.find(({ from, to }) => to === node.id);

      if (!edgeToTheLeftOfNode) {
        return {
          nodes: newNodes,
          edges: newEdges,
          actions: newActions,
        };
      }

      if (edgeToTheLeftOfNode.from !== edgeToTheRightOfNode.to) {
        const newEdge = createEdge({
          from: edgeToTheLeftOfNode.from,
          to: edgeToTheRightOfNode.to,
          // text: " ",
          fromPort: edgeToTheLeftOfNode.fromPort,
          toPort: edgeToTheRightOfNode.toPort,
          parent: parentNode.id,
        });
        newEdges.push(newEdge);
      }

      return {
        nodes: newNodes,
        edges: newEdges,
        actions: newActions,
      };
    } else if (parentNode.data?.type.toUpperCase() === "PARALLEL_GROUP") {
      // Parallel should be simple. Just remove the node and the edges connected to it
      const edgesToRetain = edges.filter(({ from, to }) => !(from === node.id || to === node.id));
      const newNodes = nodes.filter((n) => !(n.id === node.id)),
        newActions = actions.filter((action) => action.id !== node.id),
        newEdges: BaseEdgeDataType[] = [];

      newEdges.push(...edgesToRetain);
      return {
        nodes: newNodes,
        edges: newEdges,
        actions: newActions,
      };
    }
  } else {
    // Not part of any group

    if (node.data?.type.toUpperCase() === "SERIAL_GROUP" || node.data?.type.toUpperCase() === "PARALLEL_GROUP") {
      // Is it a group? If group, remove children also
      const edgesToTheLeftOfNode = edges.filter(({ from, to, parent }) => to === node.id && !parent),
        edgesToRetain = edges.filter(
          ({ from, to, parent }) => !(from === node.id || to === node.id || parent === node.id),
        );

      const newNodes = nodes.filter((n) => !(n.id === node.id || n.parent === node.id)),
        newEdges: BaseEdgeDataType[] = [];

      const newActions: UAction[] = actions.filter((act) => newNodes.some((n) => n.id === act.id));

      newEdges.push(...edgesToRetain);

      for (const edge of edgesToTheLeftOfNode) {
        const edgesToTheRightOfNode = edges.filter(({ from, parent }) => from === node.id && !parent);
        for (const edgeToTheRightOfNode of edgesToTheRightOfNode) {
          const isAlreadyConnected = areNodesConnected(edge.from, edgeToTheRightOfNode.to, newNodes, newEdges);
          if (isAlreadyConnected) {
            // console.log("Nodes already connected");
            // Connect to group end node for serial group
          } else {
            const newEdge = {
              id: `${edge.from}-${edgeToTheRightOfNode.to}`,
              from: edge.from,
              to: edgeToTheRightOfNode.to,
              // text: " ",
              parent: edgeToTheRightOfNode.parent,
              fromPort: `port-${edge.from}-start`,
              toPort: `port-${edgeToTheRightOfNode.to}-end`,
            };
            newEdges.push(newEdge);
          }
        }
      }
      return {
        nodes: newNodes,
        edges: newEdges,
        actions: newActions,
      };
    } else {
      // If not part of group, remove the node alone

      const edgesToTheLeftOfNode = edges.filter(({ from, to }) => to === node.id),
        edgesToRetain = edges.filter(({ from, to }) => !(from === node.id || to === node.id));

      const newNodes = nodes.filter((n) => !(n.id === node.id)),
        newActions = actions.filter((action) => action.id !== node.id),
        newEdges: BaseEdgeDataType[] = [];

      newEdges.push(...edgesToRetain);

      for (const edge of edgesToTheLeftOfNode) {
        const edgesToTheRightOfNode = edges.filter(({ from }) => from === node.id);

        for (const edgeToTheRightOfNode of edgesToTheRightOfNode) {
          // TODO: This is a crude check. Ideal to check if the nodes are connected via path with multiple nodes

          const isAlreadyConnected = areNodesConnected(edge.from, edgeToTheRightOfNode.to, newNodes, newEdges);

          if (isAlreadyConnected) {
            // console.log("Nodes already connected");
            // Connect to group end node for serial group
          } else {
            const newEdge = {
              id: `${edge.from}-${edgeToTheRightOfNode.to}`,
              from: edge.from,
              to: edgeToTheRightOfNode.to,
              // text: " ",
              parent: edgeToTheRightOfNode.parent,
              fromPort: `port-${edge.from}-start`,
              toPort: `port-${edgeToTheRightOfNode.to}-end`,
            };
            newEdges.push(newEdge);
          }
        }
      }
      return {
        nodes: newNodes,
        edges: newEdges,
        actions: newActions,
      };
    }
  }

  /* for (const edge of edgesToRemove) {
    const fromEdges = edges.filter(({ from }) => from === node.id);
    for (const fromEdge of fromEdges) {
      const isAlreadyConnected = newEdges.some(
        (e) =>
          (e.from === edge.from && e.to === fromEdge.to) ||
          (e.from === fromEdge.to && e.to === edge.from)
      );
      if (isAlreadyConnected) {
        // console.log("Nodes already connected");
        // Connect to group end node if exists
        if (fromEdge.parent) {
          // console.log("fromEdge", fromEdge);


        }

      } else {
        const newEdge = {
          id: `${edge.from}-${fromEdge.to}`,
          from: edge.from,
          to: fromEdge.to,
          text: " ",
          parent: fromEdge.parent,
        };
        newEdges.push(newEdge);
      }
    }
  }
 */
}

function areNodesConnected(
  fromNodeId: string | undefined,
  toNodeId: string | undefined,
  nodes: BaseNodeDataType[],
  edges: BaseEdgeDataType[],
) {
  // Check if there is a direct edge between the two nodes

  const fromNode = nodes.find((node) => node.id === fromNodeId);
  const toNode = nodes.find((node) => node.id === toNodeId);
  if (!fromNode || !toNode) {
    return false;
  }

  const directEdge = edges.find(
    (edge) => (edge.from === fromNodeId && edge.to === toNodeId) || (edge.from === toNodeId && edge.to === fromNodeId),
  );

  if (directEdge) {
    return true;
  }

  // traverse the graph to check if there is a path between the two nodes
  const visited = new Set<string>();
  const stack: BaseNodeDataType[] = [fromNode];
  while (stack.length > 0) {
    const currentNode = stack.pop();
    if (!currentNode || visited.has(currentNode.id)) {
      continue;
    }
    visited.add(currentNode.id);

    // Check if we reached the target node
    if (currentNode.id === toNode.id) {
      return true;
    }

    // Get all connected edges for the current node
    const connectedEdges = edges.filter(
      // (edge) => edge.from === currentNode.id || edge.to === currentNode.id
      (edge) => edge.from === currentNode.id,
    );

    for (const edge of connectedEdges) {
      const nextNodeId = edge.from === currentNode.id ? edge.to : edge.from;
      const nextNode = nodes.find((node) => node.id === nextNodeId);
      if (nextNode && !visited.has(nextNode.id)) {
        stack.push(nextNode);
      }
    }
  }
  return false;
}

export function addViaGroup(
  newNode: BaseNodeDataType,
  group: BaseNodeDataType,
  nodes: BaseNodeDataType[],
  edges: BaseEdgeDataType[],
  actions: UAction[],
) {
  const updatedNodes = [...nodes, { ...newNode, parent: group.id }];

  const groupNode = nodes.find((node) => node.id === group.id);

  if (!groupNode) {
    showNotification({
      id: "error",
      title: "Add via group",
      message: "Unable to find group node",
      color: "red",
    });
    return {
      edges,
      nodes,
      actions,
    };
  }

  if (groupNode.data?.type.toUpperCase() === "PARALLEL_GROUP") {
    const parentStartToNodeStartEdge = createEdge({
        from: groupNode.id,
        to: newNode.id,
        // text: " ",
        fromPort: `port-${groupNode.id}-start`,
        toPort: `port-${newNode.id}-end`,
        parent: groupNode.id,
      }),
      nodeEndToParentEndEdge = createEdge({
        from: newNode.id,
        to: groupNode.id,
        // text: "",
        fromPort: `port-${newNode.id}-start`,
        toPort: `port-${groupNode.id}-end`,
        parent: groupNode.id,
      });

    const updatedEdges = [...edges, parentStartToNodeStartEdge, nodeEndToParentEndEdge];

    const newAction: UAction = {
      id: newNode.id,
      type: newNode.data?.type as UNodeType,
      name: newNode.data?.name as string,
      definition: {
        workflow: getDefaultWorkflow(),
      },
    };

    const updatedActions = [...actions, newAction];

    return {
      edges: updatedEdges,
      nodes: updatedNodes,
      actions: updatedActions,
    };
  } else if (groupNode.data?.type.toUpperCase() === "SERIAL_GROUP") {
    // Get node of the same parent that is connected to the group end port
    const groupNodeChildren = nodes.filter((node) => node.parent === groupNode.id);
    if (groupNodeChildren.length === 0) {
      const parentStartToNodeStartEdge = createEdge({
          from: groupNode.id,
          to: newNode.id,
          // text: " ",
          fromPort: `port-${groupNode.id}-start`,
          toPort: `port-${newNode.id}-end`,
          parent: groupNode.id,
        }),
        nodeEndToParentEndEdge = createEdge({
          from: newNode.id,
          to: groupNode.id,
          // text: " ",
          fromPort: `port-${newNode.id}-start`,
          toPort: `port-${groupNode.id}-end`,
          parent: groupNode.id,
        });

      const updatedEdges = [...edges, parentStartToNodeStartEdge, nodeEndToParentEndEdge];

      const newAction: UAction = {
        id: newNode.id,
        type: newNode.data?.type as UNodeType,
        name: newNode.data?.name as string,
        definition: {
          workflow: getDefaultWorkflow(),
        },
      };

      const updatedActions = [...actions, newAction];

      return {
        edges: updatedEdges,
        nodes: updatedNodes,
        actions: updatedActions,
      };
    } else {
      let endNode: BaseNodeDataType | undefined, endNodeEdge: BaseEdgeDataType | undefined;

      for (const node of groupNodeChildren) {
        endNodeEdge = edges.find(
          (edge) =>
            (edge.from === node.id || edge.to === node.id) &&
            (edge.fromPort === `port-${groupNode.id}-end` || edge.toPort === `port-${groupNode.id}-end`),
        );

        if (endNodeEdge) {
          endNode = node;
          break;
        }
      }

      if (!endNodeEdge || !endNode) {
        showNotification({
          id: "error",
          title: "Add via group",
          message: "Unable to find node connected to group end port",
          color: "red",
        });
        return {
          edges,
          nodes,
          actions,
        };
      }

      const nodeStartEdge = createEdge({
          from: endNode.id,
          to: newNode.id,
          text: " ",
          fromPort: `port-${endNode.id}-start`,
          toPort: `port-${newNode.id}-end`,
          parent: groupNode.id,
        }),
        nodeEndEdge = createEdge({
          from: newNode.id,
          to: groupNode.id,
          text: "",
          fromPort: `port-${newNode.id}-start`,
          toPort: `port-${groupNode.id}-end`,
          parent: groupNode.id,
        });

      const updatedEdges = [...edges.filter((e) => e.id !== endNodeEdge?.id), nodeStartEdge, nodeEndEdge];

      const newAction: UAction = {
        id: newNode.id,
        type: newNode.data?.type as UNodeType,
        name: newNode.data?.name as string,
        definition: {
          workflow: getDefaultWorkflow(),
        },
      };

      const updatedActions = [...actions, newAction];

      return {
        edges: updatedEdges,
        nodes: updatedNodes,
        actions: updatedActions,
      };
    }
  } else {
    showNotification({
      id: "error",
      title: "Add via group",
      message: "Not a valid group type",
      color: "red",
    });
    return {
      edges,
      nodes,
      actions,
    };
  }
}

export function updateAction(action: any, nodes: BaseNodeDataType[], edges: BaseEdgeDataType[], actions: UAction[]) {
  console.log("updateAction", action);
  const node = nodes.find((n) => n.id === action.id),
    newNodes = nodes.filter((n) => n.id !== action.id),
    updatedActions = actions.filter((a) => a.id !== action.id);

  if (!node) {
    showNotification({
      id: "error",
      title: "Update action",
      message: "Unable to find node for the action",
      color: "red",
    });
    return {
      edges,
      nodes,
      actions,
    };
  }
  const { data, ...rest } = node;
  const updatedNodes = [
    ...newNodes,
    {
      ...rest,
      data: {
        ...data,
        name: action.name || data?.name,
        description: action.description || data?.description,
      },
    },
  ];

  updatedActions.push(action);

  return {
    edges: edges,
    nodes: updatedNodes,
    actions: updatedActions,
  };
}

/* export function updateModel(
  newNode: BaseNodeDataType,
  edge: BaseEdgeDataType,
  nodes: BaseNodeDataType[],
  edges: BaseEdgeDataType[]
) {
  const updatedNodes = [...nodes, newNode];

  // --- Insert new node beetween two existing ones
  const updatedEdges = [
    ...edges.filter((e) => e.id !== edge.id),
    createEdge({ from: edge.from, to: newNode.id, text: " " }),
    createEdge({ from: newNode.id, to: edge.to, text: " " }),
  ];

  return {
    edges: updatedEdges,
    nodes: updatedNodes,
  };
}
 */
const NODE_WIDTH = 300;
const NODE_HEIGHT = 80;
const GROUP_NODE_WIDTH = 600;
const GROUP_NODE_HEIGHT = 200;

export const createNode = (id: string, type: UNodeType, description: string, parent?: string): BaseNodeDataType => {
  if (
    type.toUpperCase() === "GROUP" ||
    type.toUpperCase() === "SERIAL_GROUP" ||
    type.toUpperCase() === "PARALLEL_GROUP"
  ) {
    return {
      id,
      parent: parent,
      width: GROUP_NODE_WIDTH,
      height: GROUP_NODE_HEIGHT,
      layoutOptions: {
        "elk.padding": "[left=100, top=100, right=100, bottom=100]",
        portConstraints: "FREE",
      },
      ports: [
        { id: `port-${id}-start`, height: 10, width: 10, side: "EAST" },
        { id: `port-${id}-end`, height: 10, width: 10, side: "WEST" },
      ],
      data: {
        name: type,
        type: type,
        description: description,
      },
    };
  } else {
    return {
      id,
      parent: parent,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      ports: [
        { id: `port-${id}-start`, height: 10, width: 10, side: "EAST" },
        { id: `port-${id}-end`, height: 10, width: 10, side: "WEST" },
      ],
      data: {
        name: type,
        type: type,
        description: description,
      },
    };
  }
};

export const createEdge = ({ from, to, ...props }: Omit<BaseEdgeDataType, "id">) => ({
  id: `${from}-${to}`,
  from,
  to,
  ...props,
});

export function getNodeWidth(type: string) {
  if (type.toUpperCase() === "GROUP") {
    return GROUP_NODE_WIDTH;
  } else if (type.toUpperCase() === "START" || type.toUpperCase() === "END") {
    return 100;
  }

  return NODE_WIDTH;
}

export function getNodeHeight(type: string) {
  if (type.toUpperCase() === "GROUP") {
    return GROUP_NODE_HEIGHT;
  } else if (type.toUpperCase() === "START" || type.toUpperCase() === "END") {
    return 100;
  }

  return NODE_HEIGHT;
}

export type workflowActionTypes = "SET" | "RESET";

export type workflowStateType = {
  action: "VIEW" | "ADD" | "DELETE" | null;
  via: "EDGE" | "PORT" | "NODE" | "GROUP" | null;
  edge?: BaseEdgeDataType | null;
  node?: BaseNodeDataType | null;
  port?: {
    from: string;
    to?: string | null;
  } | null;
};

export const workflowInitialState: workflowStateType = {
  action: null,
  via: null,
  edge: null,
  node: null,
  port: null,
};

export function workflowReducer(
  state: workflowStateType,
  action: IDispatchAction<workflowActionTypes, workflowStateType>,
) {
  switch (action.type) {
    case "SET":
      return { ...action.value };
    case "RESET":
      return workflowInitialState;
    default:
      return state;
  }
}
